import { Link } from 'react-router-dom'
import {
  FiExternalLink,
  FiClock,
  FiCheck,
  FiDollarSign,
  FiPackage,
  FiDownload,
  FiTruck,
  FiCheckCircle,
} from 'react-icons/fi'
import {
  getOrderDisplayStatus,
  formatGrade,
  formatCurrency,
  formatRelativeTime,
  formatDateTime,
  getIPFSUrl,
  getPolygonscanUrl,
} from '../utils/formatters'

export default function OrderItem({
  order,
  role = 'owner',
  imageUrl: signedImageUrl,
  isLoadingImage = false,
  onPayClick,
  onConfirmDelivery,
  onMarkShipped,
}) {
  const status = getOrderDisplayStatus(order)
  const imageUrl =
    signedImageUrl ||
    order.card?.frontImage ||
    getIPFSUrl(order.card?.metadata?.images?.frontHash) ||
    '/placeholder-card.png'

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
  const API_ROOT = API_BASE_URL.replace(/\/api\/v1$/, '')

  // Status color mapping for futuristic theme
  const getStatusStyle = (color) => {
    const styles = {
      yellow: {
        bg: 'rgba(227, 184, 114, 0.2)',
        border: 'rgba(227, 184, 114, 0.4)',
        text: '#E3B872',
      },
      orange: { bg: 'rgba(249, 115, 22, 0.2)', border: 'rgba(249, 115, 22, 0.4)', text: '#F97316' },
      green: {
        bg: 'rgba(143, 216, 230, 0.2)',
        border: 'rgba(143, 216, 230, 0.4)',
        text: '#8FD8E6',
      },
      blue: { bg: 'rgba(180, 156, 245, 0.2)', border: 'rgba(180, 156, 245, 0.4)', text: '#B49CF5' },
      purple: {
        bg: 'rgba(180, 156, 245, 0.2)',
        border: 'rgba(180, 156, 245, 0.4)',
        text: '#B49CF5',
      },
      gray: { bg: 'rgba(107, 114, 128, 0.2)', border: 'rgba(107, 114, 128, 0.4)', text: '#9CA3AF' },
      red: { bg: 'rgba(227, 184, 114, 0.2)', border: 'rgba(227, 184, 114, 0.4)', text: '#E3B872' },
    }
    return styles[color] || styles.gray
  }

  const statusStyle = getStatusStyle(status.color)

  const getActionButton = () => {
    if (role === 'grader') {
      if (order.status === 'CREATED') {
        return (
          <Link
            to={`/grader/order/${order.id}`}
            className="btn-primary btn-sm"
            onClick={(e) => e.stopPropagation()}
          >
            Set Price
          </Link>
        )
      }
      if (order.status === 'PAID') {
        return (
          <Link
            to={`/grader/order/${order.id}`}
            className="btn-primary btn-sm"
            onClick={(e) => e.stopPropagation()}
          >
            Submit Grade
          </Link>
        )
      }
    }

    if (role === 'owner') {
      const hasPrice = order.priceUSD != null || order.price != null
      const canPay =
        hasPrice &&
        !order.paid &&
        ['QUOTE_SENT', 'AWAITING_PAYMENT', 'PRICE_SET'].includes(order.status)
      if (canPay && onPayClick) {
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onPayClick(order)
            }}
            className="btn-accent btn-sm"
          >
            Pay{' '}
            {order.priceUSD != null
              ? `$${parseFloat(order.priceUSD).toFixed(2)}`
              : `${formatCurrency(order.price)} MATIC`}
          </button>
        )
      }
      if (canPay && !onPayClick) {
        return (
          <Link
            to={`/order/${order.id}/pay`}
            className="btn-accent btn-sm"
            onClick={(e) => e.stopPropagation()}
          >
            Pay{' '}
            {order.priceUSD != null
              ? `$${parseFloat(order.priceUSD).toFixed(2)}`
              : `${formatCurrency(order.price)} MATIC`}
          </Link>
        )
      }
      if (order.status === 'COMPLETED' && order.card?.status !== 'MINTED') {
        return (
          <Link
            to={`/card/${order.cardId}/mint`}
            className="btn-primary btn-sm"
            onClick={(e) => e.stopPropagation()}
          >
            Mint NFT
          </Link>
        )
      }
    }

    return null
  }

  return (
    <div className="card flex flex-col sm:flex-row gap-5 sm:gap-6">
      {/* Card Image */}
      <Link to={`/card/${order.cardId}`} className="w-full sm:w-24 flex-shrink-0">
        <div
          className="aspect-[3/4] rounded-xl overflow-hidden relative"
          style={{ background: 'rgba(16, 24, 48, 0.8)' }}
        >
          {isLoadingImage ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={order.card?.name || 'Card'}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.target.src = '/placeholder-card.png'
                e.target.onerror = null
              }}
            />
          )}
        </div>
      </Link>

      {/* Order Info */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
          <div>
            <h3 className="font-semibold text-lg">
              <Link
                to={`/card/${order.cardId}`}
                className="text-white hover:text-cyan-400 transition-colors"
              >
                {order.card?.name || 'Unknown Card'}
              </Link>
            </h3>
            <p className="text-sm text-gray-500">{order.card?.cardType}</p>
          </div>

          <span
            className="px-3 py-1 rounded-full text-sm font-medium"
            style={{
              background: statusStyle.bg,
              border: `1px solid ${statusStyle.border}`,
              color: statusStyle.text,
            }}
          >
            {status.label}
          </span>
        </div>

        {/* Order Details */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-5 sm:gap-5 text-sm mb-4">
          {/* Price */}
          <div>
            <p className="text-gray-500">Price</p>
            <p className="font-medium flex items-center gap-1 text-white">
              <FiDollarSign size={14} className="text-cyan-400" />
              {order.priceUSD != null ? (
                <>
                  <span className="text-cyan-400 font-bold">
                    ${parseFloat(order.priceUSD).toFixed(2)}
                  </span>
                  <span className="text-gray-400 text-sm">USD</span>
                </>
              ) : order.price != null ? (
                `${formatCurrency(order.price)} MATIC`
              ) : (
                'Not set'
              )}
            </p>
          </div>

          {/* Payment Status */}
          <div>
            <p className="text-gray-500">Payment</p>
            <p className="font-medium flex items-center gap-1">
              {!order.paid ? (
                <>
                  <FiClock className="text-yellow-400" size={14} />
                  <span className="text-yellow-400">Pending</span>
                </>
              ) : status.label === 'Awaiting lab review' ? (
                <>
                  <FiClock className="text-amber-400" size={14} />
                  <span className="text-amber-400">{status.label}</span>
                </>
              ) : status.label === 'Prepaid — Lab intake' ? (
                <>
                  <FiCheck className="text-emerald-400" size={14} />
                  <span className="text-emerald-400">Prepaid</span>
                </>
              ) : (
                <>
                  <FiCheck className="text-emerald-400" size={14} />
                  <span className="text-emerald-400">Paid</span>
                </>
              )}
            </p>
          </div>

          {/* Grade */}
          <div>
            <p className="text-gray-500">Grade</p>
            <p className="font-medium text-white">
              {order.gradeOverall != null || order.grade != null
                ? formatGrade(order.gradeOverall ?? order.grade)
                : 'Pending'}
            </p>
          </div>

          {/* Time */}
          <div>
            <p className="text-gray-500">Created</p>
            <p className="font-medium text-xs text-gray-400">
              {formatRelativeTime(order.createdAt)}
            </p>
          </div>
        </div>

        {/* Payment Debug (owner) */}
        {role === 'owner' && order.paid && order.payment?.txHash && (
          <div
            className="mt-2 p-3 rounded-lg"
            style={{
              background: 'rgba(143, 216, 230, 0.08)',
              border: '1px solid rgba(143, 216, 230, 0.25)',
            }}
          >
            <div className="text-xs text-emerald-400 mb-1">✅ Payment Confirmed</div>
            <a
              href={getPolygonscanUrl(order.payment.txHash) || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-cyan-400 hover:text-cyan-300 underline font-mono break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {order.payment.txHash}
            </a>
            {order.payment.to && (
              <div className="text-xs text-gray-400 mt-1">
                Paid to: {order.payment.to.slice(0, 6)}...{order.payment.to.slice(-4)}
              </div>
            )}
          </div>
        )}

        {/* Shipped to owner - tracking and confirm delivery */}
        {role === 'owner' && order.status === 'SHIPPED_TO_OWNER' && (
          <div
            className="mt-3 p-4 rounded-lg"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
            }}
          >
            <div className="flex items-center gap-2 text-blue-400 mb-2">
              <FiTruck className="w-5 h-5" />
              <span className="font-bold">On the Way!</span>
            </div>
            <p className="text-sm text-gray-300 mb-2">
              Your graded card is being shipped back to you
            </p>
            {(order.returnShippingCarrier || order.returnTrackingNumber) && (
              <div className="text-sm text-gray-400 mb-3">
                {order.returnShippingCarrier && <p>Carrier: {order.returnShippingCarrier}</p>}
                {order.returnTrackingNumber && (
                  <p className="font-mono">{order.returnTrackingNumber}</p>
                )}
              </div>
            )}
            {onConfirmDelivery && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onConfirmDelivery(order.id)
                }}
                className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-medium"
              >
                Confirm card receipt
              </button>
            )}
          </div>
        )}

        {/* Shipped by owner - card on the way to grader */}
        {role === 'owner' && order.status === 'SHIPPED_BY_OWNER' && (
          <div
            className="mt-3 p-3 rounded-lg"
            style={{
              background: 'rgba(180, 156, 245, 0.08)',
              border: '1px solid rgba(180, 156, 245, 0.4)',
            }}
          >
            <p className="text-sm text-cyan-300">Your card is on its way to the grader…</p>
            {order.ownerTrackingNumber && (
              <p className="text-xs text-gray-400 mt-1">
                Tracking:{' '}
                <span className="font-mono text-cyan-400">{order.ownerTrackingNumber}</span>
              </p>
            )}
          </div>
        )}

        {/* Delivered */}
        {role === 'owner' && order.status === 'DELIVERED' && (
          <div
            className="mt-3 p-3 rounded-lg"
            style={{
              background: 'rgba(143, 216, 230, 0.1)',
              border: '1px solid rgba(143, 216, 230, 0.4)',
            }}
          >
            <div className="flex items-center gap-2 text-green-400">
              <FiCheckCircle className="w-5 h-5" />
              <span className="font-bold">Delivered!</span>
            </div>
            {order.deliveredAt && (
              <p className="text-sm text-gray-400 mt-1">
                Completed: {formatDateTime(order.deliveredAt)}
              </p>
            )}
          </div>
        )}

        {/* Shipping instructions for owner */}
        {role === 'owner' && order.status === 'AWAITING_SHIPMENT' && order.shippingLabelUrl && (
          <div
            className="mt-3 p-3 rounded-lg"
            style={{
              background: 'rgba(249, 115, 22, 0.08)',
              border: '1px solid rgba(249, 115, 22, 0.4)',
            }}
          >
            <div className="flex items-center gap-2 text-orange-400 mb-2">
              <FiPackage size={14} />
              <span className="text-xs font-semibold">Ready to ship</span>
            </div>
            <p className="text-xs text-gray-300 mb-2">
              Download your shipping label and mail your card to M1NT Lab for grading.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href={`${API_ROOT}${order.shippingLabelUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-orange-500 text-white text-xs font-medium hover:bg-orange-600 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <FiDownload size={12} />
                Download Label
              </a>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  alert(order.shippingInstructions || 'No shipping instructions available yet.')
                }}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gray-800 text-white text-xs font-medium hover:bg-gray-700 transition-colors"
              >
                View Instructions
              </button>
              {onMarkShipped && order.status === 'AWAITING_SHIPMENT' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onMarkShipped(order)
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-semibold hover:opacity-90 transition"
                >
                  <FiPackage size={12} />
                  Mark as shipped
                </button>
              )}
            </div>
          </div>
        )}

        {/* Grader/Owner Info */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-white/5">
          <div className="text-sm">
            {role === 'owner' ? (
              <p className="text-gray-500">
                Grader:{' '}
                <span className="text-cyan-400">
                  {order.grader?.name ||
                    (order.grader?.wallet ? `${order.grader.wallet.slice(0, 8)}...` : '—')}
                </span>
              </p>
            ) : (
              <p className="text-gray-500">
                Owner:{' '}
                <span className="font-mono text-cyan-400">
                  {order.card?.owner?.wallet?.slice(0, 8)}...
                </span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {getActionButton()}

            {order.blockchainTxHash && (
              <a
                href={getPolygonscanUrl(order.blockchainTxHash) || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-500 hover:text-cyan-400 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <FiExternalLink size={16} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
